import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Drawer, Form, Input, Space, Table, Typography, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useAuth } from '../../AuthProvider';
import { baseUrl_SPB } from '../../BaseUrl';
import RetrieveCompanyList from '../../services/RetrieveCompanyList';

const { Title, Text } = Typography;

export default function ManageCompany() {
  const authContext = useAuth();
  const token = authContext.token;
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchCompanies = () => {
    if (!token) return;
    setLoading(true);
    setError('');
    RetrieveCompanyList(token)
      .then((data) => {
        setCompanies(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setError('Failed to load company list.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCompanies();
  }, [token]);

  const openEdit = (company) => {
    setEditingCompany(company);
    form.setFieldsValue({
      companyId: company.id || company.companyId || '',
      companyName: company.companyName || company.name || '',
      companyABN: company.companyABN || company.abn || '',
      email: company.email || '',
    });
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        ...editingCompany,
        companyId: values.companyId,
        companyName: values.companyName,
        companyABN: values.companyABN,
        email: values.email,
      };

      const response = await fetch(`${baseUrl_SPB}/api/v1/company/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Save failed');
      }

      message.success('Company details updated');
      setDrawerOpen(false);
      fetchCompanies();
    } catch (saveError) {
      if (saveError?.errorFields) return;
      console.error(saveError);
      message.error('Failed to update company');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'Company Name',
      key: 'companyName',
      render: (_, rec) => rec.companyName || rec.name || '-',
    },
    {
      title: 'Company ID',
      key: 'companyId',
      render: (_, rec) => rec.id || rec.companyId || '-',
    },
    {
      title: 'ABN',
      key: 'companyABN',
      render: (_, rec) => rec.companyABN || rec.abn || '-',
    },
    {
      title: 'Email',
      key: 'email',
      render: (_, rec) => rec.email || '-',
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, rec) => (
        <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(rec)}>
          Edit
        </Button>
      ),
    },
  ];

  if (authContext.role !== 'ROLE_SADMIN') {
    return <Alert type="error" showIcon message="Access denied" description="Only Super Admin can manage all companies." />;
  }

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>Manage Company</Title>
          <Text type="secondary">View all companies and update their details.</Text>
        </div>

        {error ? <Alert type="error" showIcon message={error} /> : null}

        <Card>
          <Table
            rowKey={(rec) => rec.id || rec.companyId || rec.companyName}
            dataSource={companies}
            columns={columns}
            loading={loading}
            pagination={{ pageSize: 20, showSizeChanger: true }}
          />
        </Card>
      </Space>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={editingCompany ? `Edit Company - ${editingCompany.companyName || editingCompany.name || ''}` : 'Edit Company'}
        width={520}
        extra={
          <Space>
            <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={handleSave}>Save</Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="companyId" label="Company ID">
            <Input disabled />
          </Form.Item>
          <Form.Item name="companyName" label="Company Name" rules={[{ required: true, message: 'Company name is required' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="companyABN"
            label="Company ABN"
            rules={[
              { required: true, message: 'Company ABN is required' },
              { pattern: /^\d{11}$/, message: 'ABN must be 11 digits' },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Company Email" rules={[{ type: 'email', message: 'Enter a valid email' }]}>
            <Input />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}